import { useState } from 'react';
import { PALETTE } from '../tokens';
import { paymentsApi } from '../api/payments';

const STYLES = `
  .paywall-root {
    font-family: 'DM Sans', sans-serif;
    background: ${PALETTE.bg};
    min-height: 100vh;
    min-height: 100dvh;
    color: ${PALETTE.text};
    max-width: 420px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 28px 64px;
    position: relative;
    overflow: hidden;
  }

  .display-serif { font-family: 'Instrument Serif', 'Playfair Display', Georgia, serif; }

  .paywall-wash {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 420px;
    height: 400px;
    pointer-events: none;
    z-index: 0;
    background: radial-gradient(ellipse at 50% 10%, rgba(158,132,86,0.10) 0%, transparent 72%);
  }

  .paywall-content {
    position: relative;
    z-index: 1;
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .paywall-headline {
    font-family: 'Instrument Serif', Georgia, serif;
    font-size: 36px;
    font-weight: 400;
    font-style: italic;
    color: ${PALETTE.text};
    line-height: 1.15;
    letter-spacing: -0.01em;
    margin-bottom: 16px;
    max-width: 340px;
  }

  .paywall-subheadline {
    font-size: 14px;
    font-style: italic;
    color: ${PALETTE.primaryMid || PALETTE.textMid};
    line-height: 1.65;
    font-weight: 300;
    max-width: 300px;
    margin-bottom: 28px;
  }

  .paywall-body {
    font-size: 13.5px;
    color: ${PALETTE.textMid};
    line-height: 1.78;
    font-weight: 300;
    max-width: 320px;
    margin-bottom: 36px;
  }

  .paywall-pricing-block {
    background: ${PALETTE.surface};
    border-radius: 20px;
    padding: '24px 28px';
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    margin-bottom: 20px;
    width: 100%;
    max-width: 340px;
  }

  .paywall-price-label {
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${PALETTE.textMute};
    font-weight: 500;
    margin-bottom: 8px;
  }

  .paywall-price {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 32px;
    font-weight: 400;
    font-style: italic;
    color: ${PALETTE.text};
    line-height: 1;
    margin-bottom: 4px;
  }

  .paywall-price-subtext {
    font-size: 12px;
    color: ${PALETTE.textMid};
    font-weight: 300;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  .paywall-cta-btn {
    background: ${PALETTE.primary};
    color: ${PALETTE.bg};
    border: none;
    border-radius: 14px;
    padding: 15px 32px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    letter-spacing: 0.01em;
    transition: opacity 0.15s;
    width: 100%;
    max-width: 280px;
    margin: 0 auto 12px;
  }

  .paywall-cta-btn:hover { opacity: 0.85; }
  .paywall-cta-btn:active { opacity: 0.72; }
  .paywall-cta-btn:disabled { opacity: 0.5; cursor: default; }

  .paywall-disclaimer {
    font-size: 12px;
    color: ${PALETTE.textMute};
    font-weight: 300;
  }

  .paywall-closer {
    font-size: 12.5px;
    font-style: italic;
    color: ${PALETTE.textMute};
    font-weight: 300;
    line-height: 1.6;
    max-width: 300px;
    margin-top: 24px;
  }
`;

export default function PaywallView({ archetype = null }) {
  const [loading, setLoading] = useState(false);

  const handleUnlock = async () => {
    setLoading(true);
    try {
      const response = await paymentsApi.createCheckoutSession();
      if (response?.checkout_url) {
        window.location.href = response.checkout_url;
      }
    } catch (err) {
      console.error('Checkout session creation failed:', err);
      setLoading(false);
    }
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="paywall-root">
        <div className="paywall-wash" />

        <div className="paywall-content">
          {/* Headline — continues the reveal moment by naming the
              archetype they just met. Falls back to a generic line if the
              archetype hasn't been determined (or determination failed). */}
          <h1 className="paywall-headline">
            {archetype
              ? `You just met your ${archetype}.`
              : 'You just saw your first seven days.'}
          </h1>

          {/* Subheadline */}
          <p className="paywall-subheadline">
            That's who you've been for the last seven days. This is where it
            starts to compound.
          </p>

          {/* Body copy */}
          <p className="paywall-body">
            Haiven read your patterns across body, mind, and behavior and
            showed you something you couldn't see from the inside. That was
            seven days. The signal gets sharper the longer you stay —
            week-over-week patterns, the connections between your domains, the
            things that move you before you notice them.
            <br /><br />
            Founding members lock this in now, at the rate it'll never be again.
          </p>

          {/* Pricing block — same structure, continuation framing */}
          <div className="paywall-pricing-block">
            <div className="paywall-price-label">Founding Member</div>
            <div className="paywall-price">$39 / month</div>
            <div className="paywall-price-subtext">Locked for life</div>
            <button
              className="paywall-cta-btn"
              onClick={handleUnlock}
              disabled={loading}
            >
              {loading ? 'Opening checkout...' : 'Continue as a founding member'}
            </button>
            <div className="paywall-disclaimer">Cancel anytime.</div>
          </div>

          {/* Quiet closer in place of the old blurred teaser — the user
              already saw their domain rows unblurred during the reveal, so
              re-blurring them would be insulting. */}
          <p className="paywall-closer">
            Day 8 is where most people start to see it.
          </p>
        </div>
      </div>
    </>
  );
}
